import React from "react";
import type { LucideIcon } from "lucide-react";
import { Tooltip } from "./Tooltip";

interface IconActionButtonProps {
  /** Icon component from lucide-react */
  icon: LucideIcon;
  /** Callback function when the button is clicked */
  onClick: () => void;
  /** Title/tooltip text for the button */
  title: string;
  /** Optional additional CSS classes */
  className?: string;
  /** Icon size in pixels */
  iconSize?: number;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Whether to show tooltip with info icon instead of native title */
  showTooltip?: boolean;
}

/**
 * IconActionButton
 * Reusable button component for icon-based actions in tables.
 * Displays any lucide-react icon with consistent styling and hover effects.
 * Supports custom tooltips with optional info icon.
 */
export const IconActionButton: React.FC<IconActionButtonProps> = ({
  icon: Icon,
  onClick,
  title,
  className = "",
  iconSize = 18,
  disabled = false,
  showTooltip = false,
}) => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-gray-800 hover:text-blue-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      title={showTooltip ? undefined : title}
      aria-label={title}
    >
      <Icon size={iconSize} />
    </button>
  );

  if (showTooltip) {
    return (
      <div className="flex items-center gap-1">
        {button}
        <Tooltip content={title} showInfoIcon />
      </div>
    );
  }

  return button;
};

